import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, StyledEngineProvider, CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import theme from './theme';
import { HelmetProvider } from 'react-helmet-async';

// Shared providers; route components below load on demand.
// רכיבים נוספים
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import NoIndexHelmet from './components/common/NoIndexHelmet';
import { AuthProvider } from './contexts/AuthContext';
import { PublicLanguageProvider } from './contexts/PublicLanguageContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { FilterProvider } from './contexts/FilterContext';

const HomePage = React.lazy(() => import(/* webpackChunkName: "route-HomePage" */ './pages/public-site/HomePage'));
const AirportPolicyPage = React.lazy(() => import(/* webpackChunkName: "route-AirportPolicyPage" */ './pages/public-site/AirportPolicyPage'));
const SearchResultsPage = React.lazy(() => import(/* webpackChunkName: "route-SearchResultsPage" */ './pages/public-site/SearchResultsPage'));
const GalleryPage = React.lazy(() => import(/* webpackChunkName: "route-GalleryPage" */ './pages/public-site/GalleryPage'));
const FAQDetailsPage = React.lazy(() => import(/* webpackChunkName: "route-FAQDetailsPage" */ './pages/public-site/FAQDetailsPage'));
const RothschildHomePage = React.lazy(() => import(/* webpackChunkName: "route-RothschildHomePage" */ './pages/public-site/RothschildHomePage'));
const RothschildSearchResultsPage = React.lazy(() => import(/* webpackChunkName: "route-RothschildSearchResultsPage" */ './pages/public-site/RothschildSearchResultsPage'));
const RothschildGalleryPage = React.lazy(() => import(/* webpackChunkName: "route-RothschildGalleryPage" */ './pages/public-site/RothschildGalleryPage'));
const RothschildFAQDetailsPage = React.lazy(() => import(/* webpackChunkName: "route-RothschildFAQDetailsPage" */ './pages/public-site/RothschildFAQDetailsPage'));
const RothschildPolicyPage = React.lazy(() => import(/* webpackChunkName: "route-RothschildPolicyPage" */ './pages/public-site/RothschildPolicyPage'));

// מערכת הניהול נטענת רק כשנכנסים אליה — כך שאורח באתר הציבורי לא מוריד אותה (מהירות טעינה)
const GuestStayPage = React.lazy(() => import(/* webpackChunkName: "route-GuestStayPage" */ './pages/guest/GuestStayPage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Bookings = React.lazy(() => import('./pages/Bookings'));
const Login = React.lazy(() => import('./pages/Login'));
const Settings = React.lazy(() => import('./pages/settings/Settings'));
const RoomPricing = React.lazy(() => import('./pages/settings/rooms/RoomPricing'));
// The pricing page was merged into the rooms page; keep old links working.
const RoomPricingRedirect = () => <Navigate to={`/settings/rooms/${useParams().location}`} replace />;
const DiscountSettings = React.lazy(() => import('./pages/settings/DiscountSettings'));
const SyncSettings = React.lazy(() => import('./pages/settings/SyncSettings'));
const Beds24Settings = React.lazy(() => import('./pages/settings/Beds24Settings'));
const MediaSecurity = React.lazy(() => import('./pages/settings/MediaSecurity'));
const J5Test = React.lazy(() => import('./pages/settings/J5Test')); // זמני - בדיקת חסימת מסגרת
const Cleaning = React.lazy(() => import('./pages/Cleaning'));
const MonthlyRevenue = React.lazy(() => import('./pages/revenue/MonthlyRevenue'));
const FinancialOverview = React.lazy(() => import('./pages/revenue/FinancialOverview'));
const Reconciliation = React.lazy(() => import('./pages/revenue/Reconciliation'));
const CapitalManagement = React.lazy(() => import('./pages/revenue/CapitalManagement'));
const EmailPreview = React.lazy(() => import('./pages/email/EmailPreview'));
const Inquiries = React.lazy(() => import('./pages/Inquiries'));
const Calls = React.lazy(() => import('./pages/Calls'));
const RothschildConfirmationPage = React.lazy(() => import('./pages/public-site/RothschildConfirmationPage'));
const RothschildBookingFormPage = React.lazy(() => import(/* webpackChunkName: "route-RothschildBookingFormPage" */ './pages/public-site/RothschildBookingFormPage'));
const ConfirmationPage = React.lazy(() => import('./pages/public-site/ConfirmationPage'));
const BookingFormPage = React.lazy(() => import(/* webpackChunkName: "route-BookingFormPage" */ './pages/public-site/BookingFormPage'));
const PublicNoticeBoard = React.lazy(() => import('./pages/public-site/PublicNoticeBoard'));
const Layout = React.lazy(() => import('./components/common/Layout'));

// דף הבית של אור יהודה: /airport-booking בעברית, /airport-booking/en באנגלית.
// הכתובת קובעת את השפה - אין הפניה אוטומטית לפי שפת הדפדפן.
// המשתמש יכול לשנות שפה דרך מתג השפה בממשק.
const AirportHomeRoute = ({ lang }) => {
  return (
    <PublicLanguageProvider forcedLanguage={lang}>
      <HomePage />
    </PublicLanguageProvider>
  );
};

// דף הבית של רוטשילד: /rothschild-booking בעברית (ברירת המחדל), /rothschild-booking/en באנגלית.
// הכתובת קובעת את השפה - אין הפניה אוטומטית לפי שפת הדפדפן.
// המשתמש יכול לשנות שפה דרך מתג השפה בממשק.
const RothschildHomeRoute = ({ lang }) => {
  return (
    <PublicLanguageProvider forcedLanguage={lang}>
      <RothschildHomePage />
    </PublicLanguageProvider>
  );
};

// מסך המתנה קצר בזמן שחלק של המערכת נטען
const RouteLoading = () => (
  <div role="status" aria-live="polite" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4b5563', fontFamily: 'Heebo, Arial, sans-serif' }}>
    טוען...
  </div>
);

// יצירת cache רק ל-RTL (דפי הניהול)
const createEmotionCache = () => {
  return createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
  });
};

// רכיב עוטף עם RTL קבוע לדפי הניהול
const AppContent = () => {
  const cache = React.useMemo(() => createEmotionCache(), []);

  // הגדרת כיוון RTL קבוע עבור דפי הניהול
  React.useEffect(() => {
    // נקבע RTL בכל מקרה בתחילה
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'he';
    
    // האזנה לשינויי נתיב כדי לשחזר RTL אחרי יציאה מהאתרים הציבוריים
    const handleRouteChange = () => {
      const isPublicSite = window.location.pathname.startsWith('/airport-booking') || 
                           window.location.pathname.startsWith('/rothschild-booking') || /^\/stay\/?$/.test(window.location.pathname);
      
      // אם לא באתר ציבורי, תמיד נחזיר ל-RTL
      if (!isPublicSite) {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'he';
      }
    };
    
    // ביצוע הבדיקה גם כאשר האתר נטען
    handleRouteChange();
    
    // האזנה לשינויי history
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  return (
    <CacheProvider value={cache} key="rtl">
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorBoundary>
          <AuthProvider>
            <FilterProvider>
              <LanguageProvider>
                <HelmetProvider>
                <React.Suspense fallback={<RouteLoading />}>
                <Routes>
                <Route path="/stay" element={<GuestStayPage />} />
                {/* ניתוב לדף התחברות */}
                <Route path="/login" element={<NoIndexHelmet><Login /></NoIndexHelmet>} />
                
                {/* דף ניקיון - ללא אבטחה */}
                <Route path="/cleaning" element={<Cleaning />} />
                
                {/* לוח מודעות ציבורי - ללא אבטחה */}
                <Route path="/notice-board-public" element={<PublicNoticeBoard />} />
                
                {/* נתיבי האתר הציבורי - עם קונטקסט שפה מבודד */}
                <Route path="/airport-booking" element={<AirportHomeRoute lang="he" />} />
                <Route path="/airport-booking/en" element={<AirportHomeRoute lang="en" />} />
                <Route path="/airport-booking/accessibility" element={
                  <PublicLanguageProvider>
                    <AirportPolicyPage kind="accessibility" />
                  </PublicLanguageProvider>
                } />
                <Route path="/airport-booking/terms" element={
                  <PublicLanguageProvider>
                    <AirportPolicyPage kind="terms" />
                  </PublicLanguageProvider>
                } />
                <Route path="/airport-booking/search-results" element={
                  <PublicLanguageProvider>
                    <SearchResultsPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/airport-booking/book" element={
                  <PublicLanguageProvider>
                    <BookingFormPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/airport-booking/confirmation" element={
                  <PublicLanguageProvider>
                    <ConfirmationPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/airport-booking/gallery" element={
                  <PublicLanguageProvider forcedLanguage="he">
                    <GalleryPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/airport-booking/faq-details" element={
                  <PublicLanguageProvider forcedLanguage="he">
                    <FAQDetailsPage />
                  </PublicLanguageProvider>
                } />
                
                {/* נתיבי האתר הציבורי של רוטשילד - עם קונטקסט שפה מבודד */}
                <Route path="/rothschild-booking" element={<RothschildHomeRoute lang="he" />} />
                <Route path="/rothschild-booking/en" element={<RothschildHomeRoute lang="en" />} />
                <Route path="/rothschild-booking/accessibility" element={
                  <PublicLanguageProvider>
                    <RothschildPolicyPage kind="accessibility" />
                  </PublicLanguageProvider>
                } />
                <Route path="/rothschild-booking/terms" element={
                  <PublicLanguageProvider>
                    <RothschildPolicyPage kind="terms" />
                  </PublicLanguageProvider>
                } />
                <Route path="/rothschild-booking/search-results" element={
                  <PublicLanguageProvider>
                    <RothschildSearchResultsPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/rothschild-booking/book" element={
                  <PublicLanguageProvider>
                    <RothschildBookingFormPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/rothschild-booking/confirmation" element={
                  <PublicLanguageProvider>
                    <RothschildConfirmationPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/rothschild-booking/gallery" element={
                  <PublicLanguageProvider forcedLanguage="he">
                    <RothschildGalleryPage />
                  </PublicLanguageProvider>
                } />
                <Route path="/rothschild-booking/faq-details" element={
                  <PublicLanguageProvider forcedLanguage="he">
                    <RothschildFAQDetailsPage />
                  </PublicLanguageProvider>
                } />
                
                {/* ניתובים לעמודים מוגנים */}
                <Route
                  path="/"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout contentSx={{ margin: 0, minWidth: 0, height: '100dvh', minHeight: 0, overflow: 'hidden', boxSizing: 'border-box', '@media (max-width: 899px)': { height: '100dvh', minHeight: 0, padding: 'max(12px, env(safe-area-inset-top)) 12px max(8px, env(safe-area-inset-bottom))', overflow: 'hidden', overscrollBehavior: 'none', boxSizing: 'border-box' } }}>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout contentSx={{ margin: 0, minWidth: 0, height: '100dvh', minHeight: 0, overflow: 'hidden', boxSizing: 'border-box', '@media (max-width: 899px)': { height: '100dvh', minHeight: 0, padding: 'max(12px, env(safe-area-inset-top)) 12px max(8px, env(safe-area-inset-bottom))', overflow: 'hidden', overscrollBehavior: 'none', boxSizing: 'border-box' } }}>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route path="/dashboard-new" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/bookings"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <Bookings />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/settings/j5-test"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <J5Test />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/settings/media-security"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <MediaSecurity />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/settings/rooms/:location/pricing"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout><RoomPricingRedirect /></Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/settings/rooms/:location"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <RoomPricing />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route path="/settings/rooms" element={<Navigate to="/settings/rooms/airport" replace />} />
                <Route
                  path="/settings/discounts"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <DiscountSettings />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/settings/sync"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <SyncSettings />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/settings/beds24"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <Beds24Settings />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />


                <Route
                  path="/revenue"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <MonthlyRevenue />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/financial-overview"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <FinancialOverview />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/revenue/monthly"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <MonthlyRevenue />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/revenue/overview"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <FinancialOverview />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route
                  path="/capital"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <CapitalManagement />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                <Route path="/reconciliation" element={<NoIndexHelmet><ProtectedRoute><Layout><Reconciliation /></Layout></ProtectedRoute></NoIndexHelmet>} />
                <Route path="/inquiries" element={<NoIndexHelmet><ProtectedRoute><Layout><Inquiries /></Layout></ProtectedRoute></NoIndexHelmet>} />
                <Route path="/calls" element={<NoIndexHelmet><ProtectedRoute><Layout><Calls /></Layout></ProtectedRoute></NoIndexHelmet>} />
                <Route
                  path="/email-preview"
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Layout>
                          <EmailPreview />
                        </Layout>
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  }
                />
                
                {/* ניתוב ברירת מחדל - רק למשתמשים מחוברים */}
                <Route 
                  path="*" 
                  element={
                    <NoIndexHelmet>
                      <ProtectedRoute>
                        <Navigate to="/dashboard" replace />
                      </ProtectedRoute>
                    </NoIndexHelmet>
                  } 
                />
                </Routes>
                </React.Suspense>
              </HelmetProvider>
            </LanguageProvider>
          </FilterProvider>
        </AuthProvider>
        </ErrorBoundary>
        </ThemeProvider>
      </StyledEngineProvider>
    </CacheProvider>
  );
};

/**
 * App component - הרכיב הראשי של האפליקציה
 */
function App() {
  return <AppContent />;
}

export default App;

